import { FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiTrendingUp,
  FiTrendingDown,
  FiShield,
  FiServer,
  MdOutlineGroup,
  HiOutlineUserGroup,
  MdOutlineCheckCircle,
  MdOutlineWarning,
  MdOutlineError,
} from '@/config/icons';
import { dashboardService } from '@/services/dashboardService';
import { Loading } from '@/components/Common/Loading';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import type { 
  DashboardSummary, 
  DashboardKPI,
  CertificateMetricsResponse,
  AgentMetricsResponse 
} from '@/types/dashboard';
import styles from './DashboardPage.module.css';

/**
 * DashboardPage Component
 * Main dashboard with overview statistics and real metrics
 */
const DashboardPage: FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await dashboardService.getDashboardSummary();
      setDashboardData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
      toast.error('Failed to load dashboard metrics');
      console.error('Dashboard load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getKPIStats = (
    certificateMetrics: CertificateMetricsResponse,
    agentMetrics: AgentMetricsResponse
  ): DashboardKPI[] => [
    {
      title: t('dashboard.totalCertificates'),
      value: certificateMetrics.total.toLocaleString(),
      change: `${certificateMetrics.active} ${t('common.active').toLowerCase()}`,
      changeType: 'positive' as const,
      icon: FiShield,
      color: 'primary',
    },
    {
      title: t('dashboard.certificatesExpiringSoon'),
      value: certificateMetrics.expiringIn30Days.toLocaleString(),
      change: t('dashboard.next30Days'),
      changeType: certificateMetrics.expiringIn30Days > 0 ? 'warning' : 'positive' as const,
      icon: MdOutlineWarning,
      color: 'warning',
    },
    {
      title: t('dashboard.totalAgents'),
      value: agentMetrics.total.toLocaleString(),
      change: `${agentMetrics.online} ${t('dashboard.online')}`,
      changeType: 'positive' as const,
      icon: FiServer,
      color: 'primary',
    }
  ];

  if (isLoading) {
    return <Loading />;
  }

  if (error || !dashboardData) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.header}>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>
            Welcome to ALZ Token Portal. Here's your overview.
          </p>
        </div>
        <div className={styles.errorState}>
          <p>{t('dashboard.failedToLoad')}: {error}</p>
          <button onClick={loadDashboardData} className={styles.retryButton}>
            {t('dashboard.retry')}
          </button>
        </div>
      </div>
    );
  }

  const { certificateMetrics, agentMetrics } = dashboardData;
  const stats = getKPIStats(certificateMetrics, agentMetrics);

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('dashboard.title')}</h1>
        <p className={styles.subtitle}>
          {t('dashboard.subtitle')}
        </p>
      </div>

      <div className={styles.statsGrid}>
        {stats.map((stat, index) => (
          <div key={index} className={`${styles.statCard} ${styles[stat.color || 'primary']}`}>
            <div className={styles.statIcon}>
              <stat.icon className="w-6 h-6" />
            </div>
            <div className={styles.statContent}>
              <h3 className={styles.statTitle}>{stat.title}</h3>
              <div className={styles.statValue}>{stat.value}</div>
              <div 
                className={`${styles.statChange} ${styles[stat.changeType || 'neutral']}`}
              >
                {stat.changeType === 'positive' && (
                  <FiTrendingUp className="w-4 h-4 mr-1 inline" />
                )}
                {stat.changeType === 'negative' && (
                  <FiTrendingDown className="w-4 h-4 mr-1 inline" />
                )}
                {stat.changeType === 'warning' && (
                  <MdOutlineWarning className="w-4 h-4 mr-1 inline" />
                )}
                {stat.change}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.content}>
        <div className={styles.grid}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('dashboard.certificateStatusDistribution')}</h2>
            <div className={styles.cardContent}>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem}>
                  <MdOutlineCheckCircle className="w-5 h-5 text-success" />
                  <span>{t('common.active')}: {certificateMetrics.statusDistribution.ACTIVE}</span>
                </div>
                <div className={styles.statusItem}>
                  <MdOutlineWarning className="w-5 h-5 text-warning" />
                  <span>{t('dashboard.expired')}: {certificateMetrics.statusDistribution.EXPIRED}</span>
                </div>
                <div className={styles.statusItem}>
                  <MdOutlineError className="w-5 h-5 text-error" />
                  <span>{t('dashboard.revoked')}: {certificateMetrics.statusDistribution.REVOKED}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('dashboard.topOrganizationsCertificates')}</h2>
            <div className={styles.cardContent}>
              {certificateMetrics.byOrganization.length > 0 ? (
                <div className={styles.organizationList}>
                  {certificateMetrics.byOrganization.slice(0, 5).map((org) => (
                    <div key={org.organizationSlug} className={styles.organizationItem}>
                      <span className={styles.orgName}>{org.organizationName}</span>
                      <span className={styles.orgCount}>{org.certificateCount}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p>{t('dashboard.noCertificateData')}</p>
              )}
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('dashboard.expirationTimeline')}</h2>
            <div className={styles.cardContent}>
              {certificateMetrics.expirationTimeline.length > 0 ? (
                <div className={styles.timelineList}>
                  {certificateMetrics.expirationTimeline.slice(0, 6).map((timeline) => (
                    <div key={timeline.yearMonth} className={styles.timelineItem}>
                      <span className={styles.timelineDate}>{timeline.yearMonth}</span>
                      <span className={styles.timelineCount}>{timeline.count} {t('dashboard.expiring')}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p>{t('dashboard.noExpirationData')}</p>
              )}
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('dashboard.systemInformation')}</h2>
            <div className={styles.cardContent}>
              <div className={styles.systemInfo}>
                <div className={styles.infoRow}>
                  <span>{t('dashboard.certificatesWithPassword')}:</span>
                  <span>{certificateMetrics.withPassword}/{certificateMetrics.total}</span>
                </div>
                <div className={styles.infoRow}>
                  <span>{t('dashboard.recentlyAddedAgents')}:</span>
                  <span>{agentMetrics.recentlyAdded}</span>
                </div>
                <div className={styles.infoRow}>
                  <span>{t('dashboard.certificatesWithoutSerial')}:</span>
                  <span>{certificateMetrics.withoutSerialNumber}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t('dashboard.quickActions')}</h2>
            <div className={styles.cardContent}>
              <div className={styles.actionButtons}>
                <Link to="/groups" className={styles.actionButton}>
                  <HiOutlineUserGroup className="w-4 h-4 mr-2" /> 
                  Adicionar Grupo
                </Link>
                <Link to="/users" className={styles.actionButton}>
                  <MdOutlineGroup className="w-4 h-4 mr-2" /> 
                  Adicionar Usuário
                </Link>
                <Link to="/certificates" className={styles.actionButton}>
                  <FiShield className="w-4 h-4 mr-2" /> 
                  Adicionar Certificado
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;